import React from 'react';
import { API } from 'aws-amplify';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { t } from 'i18next';

import { basicReducer as reducer } from '../../../../utils/reducers';
import { ProtectedRouteContext } from '../../../Context';

import useStyles from './styles';

const useFees = () => {
    const [state, dispatch] = React.useReducer(reducer, {});
    const { userSettings } = React.useContext(ProtectedRouteContext);

    const styles = useStyles();

    const totalFees = (fees) =>
        fees?.map?.((fee) => parseFloat(fee?.Amount?.replace?.(',', '')) || fee?.Amount)?.reduce?.((acc, currVal) => acc + currVal, 0);

    let validateDescription = (_fee) => {
        let error = false,
            errorMessage = null;
        if (_fee.Description === '') {
            error = true;
            errorMessage = 'Fee description is required.';
        } else if(_fee.Description.length > 50) {
            error = true;
            errorMessage = 'Fee description must be less than or equal to 50 characters.';
        }

        return {
            error,
            errorMessage,
        };
    };

    let validateAmount = (_fee) => {
        let error = false,
            errorMessage = null;
        let currencyRegex = new RegExp(/^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{1,2})?$/g);
        if(_fee?.Amount <= 0) {
            error = true;
            errorMessage = 'Required.'
        } else if (!currencyRegex.test(_fee.Amount)) {
            error = true;
            errorMessage = 'Only numerical, commas and dots are allowed.';
        }

        return {
            error,
            errorMessage,
        };
    };

    let validateDiscount = (discount) => {
        let error = false,
            errorMessage = null;
        let currencyRegex = new RegExp(/^[+-]?[0-9]{1,3}(?:,?[0-9]{3})*(?:\.[0-9]{1,2})?$/g);

        if (typeof discount === 'string' && discount !== '' && !currencyRegex.test(discount)) {
            error = true;
            errorMessage = 'Only numerical, commas and dots are allowed.';
        }

        return {
            error,
            errorMessage,
        };
    };

    let stripCommas = (amount) => amount?.replace?.(/,/g, '') || amount;

    const initialize = (fees, discount, attendanceDate) => {
        let _parsedFees = fees?.map?.(fee => {
            let {
                error: descErr, errorMessage: descErrMsg
            } = validateDescription(fee);

            let {
                error: amtErr, errorMessage: amtErrMsg
            } = validateAmount(fee);

            return {
                ...fee,
                amountError: amtErr,
                amountErrorMessage: amtErrMsg,
                descriptionError: descErr,
                descriptionErrorMessage: descErrMsg,
            };
        });

        let {
            error: discErr,
            errorMessage: discErrMsg
        } = validateDiscount(discount);

        dispatch({
            type: 'update',
            newState: {
                fees: _parsedFees,
                initialFees: _parsedFees,
                discount,
                discountError: discErr,
                discountErrorMessage: discErr ? discErrMsg : null,
                showDiscountError: state?.showDiscountError || false,
                attendanceDate: attendanceDate !== null ? moment.tz(attendanceDate, userSettings.ClientTimeZone) : null,
                dateError: attendanceDate === null || attendanceDate?.isValid?.() === false,
                dateErrorMessage: attendanceDate === null ? 'First Day of Attendance is required.' : null,
                showDateError: state?.showDateError || false,
                feeTotal: totalFees(fees) + discount,
            },
        });
    };

    const addFee = async (schoolId, appInstId) => {
        dispatch({
            type: 'doingAjax',
        });

        try {
            await API.post('ShipyardAPI', '/finances/annual_statement/fee', {
                body: {
                    schoolId,
                    appInstId,
                },
            });

            let _newFees = await API.post('ShipyardAPI', '/finances/annual_statement/fees', {
                body: {
                    schoolId,
                    appInstId,
                },
            });
            
            initialize(_newFees, state.discount, state.attendanceDate);
        } catch (e) {
            toast.error('API error.');
            console.error('APIError', e);
        } finally {
            dispatch({
                type: 'ajaxDone',
            });
        }
    };

    const handleFeeDescription = (feeId, description) => {
        dispatch({
            type: 'update',
            newState: {
                fees: state?.fees?.map?.((fee) => {
                    if (fee?.AppInstStmtFeeID === feeId) {
                        return {
                            ...fee,
                            Description: description,
                            showDescriptionError: false,
                        };
                    } else return fee;
                }),
            },
        });
    };

    const handleDescriptionBlur = (appInstStmtFeeId) => {
        let relevantFee = state.fees?.find?.((fee) => fee?.AppInstStmtFeeID === appInstStmtFeeId);
        let { error, errorMessage } = validateDescription(relevantFee);

        dispatch({
            type: 'update',
            newState: {
                fees: state.fees.map((fee) => {
                    if (fee?.AppInstStmtFeeID === appInstStmtFeeId) {
                        return {
                            ...fee,
                            descriptionError: error,
                            descriptionErrorMessage: errorMessage,
                            showDescriptionError: true,
                        };
                    } else return fee;
                }),
            },
        });
    };

    const handleFeeAmount = (feeId, amount) => {
        let newFees = state?.fees?.map?.((fee) => {
            if (fee?.AppInstStmtFeeID === feeId) {
                return {
                    ...fee,
                    Amount: amount,
                    showAmountError: false,
                };
            } else return fee;
        });

        dispatch({
            type: 'update',
            newState: {
                fees: newFees,
            },
        });
    };

    const handleAmountBlur = (appInstStmtFeeId) => {
        let relevantFee = state.fees?.find?.((fee) => fee?.AppInstStmtFeeID === appInstStmtFeeId);
        let { error, errorMessage } = validateAmount(relevantFee);

        let amountNoCommas = stripCommas(relevantFee?.Amount);

        dispatch({
            type: 'update',
            newState: {
                fees: state.fees?.map((fee) => {
                    if (fee?.AppInstStmtFeeID === appInstStmtFeeId) {
                        return {
                            ...fee,
                            Amount: error ? relevantFee.Amount : parseFloat(amountNoCommas),
                            amountError: error,
                            amountErrorMessage: errorMessage,
                            showAmountError: true,
                        };
                    } else return fee;
                }),
            },
        });
    };

    const saveRow = async (event, appInstStmtFeeId, appInstStmtId) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            let relevantFee = state.fees?.find?.((fee) => fee.AppInstStmtFeeID === appInstStmtFeeId);
            let initialRelevantFee = state.initialFees?.find?.((fee) => fee.AppInstStmtFeeID === appInstStmtFeeId);

            let feeInvalid = validateDescription(relevantFee)?.error === true || validateAmount(relevantFee)?.error === true;
            let feeUnchanged =
                relevantFee?.Amount === initialRelevantFee?.Amount && relevantFee?.Description === initialRelevantFee?.Description;

            if (feeInvalid || feeUnchanged) return;

            let amountNoCommas = stripCommas(relevantFee?.Amount);

            dispatch({
                type: 'doingAjax',
            });

            try {
                await API.patch('ShipyardAPI', '/finances/annual_statement/fee', {
                    body: {
                        appInstStmtId,
                        description: relevantFee.Description,
                        amount: parseFloat(amountNoCommas),
                        isTuition: relevantFee.IsTuition,
                        ordinal: relevantFee.Ordinal,
                    },
                });

                let updated = relevantFee?.Ordinal === 1 ? 'tuition' : `fee ${relevantFee.Ordinal - 1}`;

                initialize(state.fees, state.discount, state.attendanceDate);

                toast.success(`Successfully updated ${updated}.`);
            } catch (e) {
                toast.error('API error.');
            } finally {
                dispatch({
                    type: 'ajaxDone',
                });
            }
        } else return;
    };

    const handleDiscountChange = (newDisc) => {
        dispatch({
            type: 'update',
            newState: {
                discount: newDisc,
                showDiscountError: false,
            },
        });
    };

    const handleDiscountBlur = async (schoolId, appInstId) => {
        dispatch({
            type: 'doingAjax',
        });

        let discountNoCommas = stripCommas(state?.discount || 0);
        
        let {
            error: discErr, errorMessage: discErrMsg
        } = validateDiscount(state?.discount);

        let parsedDiscount = !discErr ? parseFloat(discountNoCommas) * -1 : state?.discount;

        try {            
            dispatch({
                type: 'update',
                newState: {
                    discount: parsedDiscount,
                    discountError: discErr,
                    discountErrorMessage: discErrMsg,
                    showDiscountError: true,
                }
            });
            
            if(!discErr) {
                let body = {
                    appInstId,
                    schoolId,
                    discount: discErr ? null : parsedDiscount,
                };
                let { message } = await API.patch('ShipyardAPI', '/finances/annual_statement/discount', {
                    body,
                });
    
                initialize(state.fees, parsedDiscount, state.attendanceDate);
    
                toast.success(message);
            }
        } catch (e) {
            toast.error('API error.');
        } finally {
            
            dispatch({
                type: 'ajaxDone',
            });
        }
    };

    const autopopFees = async (autopop, appInstId, gradeLevelId, schoolId) => {
        try {
            if (autopop) {
                dispatch({
                    type: 'doingAjax'
                });

                await API.post('ShipyardAPI', '/finances/annual_statement/autopopulate_fee', {
                    body: {
                        applicationId: parseInt(appInstId),
                        gradeLevelId: parseInt(gradeLevelId),
                    },
                });

                let _newFees = await API.post('ShipyardAPI', '/finances/annual_statement/fees', {
                    body: {
                        schoolId,
                        appInstId,
                    },
                });

                initialize(_newFees, state.discount, state.attendanceDate);
            } else {
                toast.error(t('AnnualStatements.AutoPopError'));
            }
        } catch (error) {
            toast.error(t('General.AjaxError'));
        } finally {
            dispatch({
                type: 'ajaxDone'
            });
        }
    };

    const deleteFee = async (appInstStmtId, ordinal, appInstId, schoolId) => {
        try {
            var { success } = await API.del('ShipyardAPI', `/finances/annual_statement/${appInstStmtId}`, {
                queryStringParameters: {
                    ordinal,
                },
            });

            toast.success('Fee deleted successfully.');
        } catch (e) {
            toast.error('API error.');
            console.error('APIError', e);
        }

        try {
            if (success) {
                let _fees = await API.post('ShipyardAPI', '/finances/annual_statement/fees', {
                    body: {
                        schoolId,
                        appInstId,
                    },
                });

                initialize(_fees, state?.discount, state?.attendanceDate);
            }
        } catch (e) {
            toast.error('API error.');
            console.error(e);
        }
    };

    const handleAttendanceDateChange = (_date) => {
        let newState = {
            attendanceDate: _date,
            showDateError: false,
        };

        if(_date === null) {
            newState.dateError = true;
            newState.dateErrorMessage = 'First Day of Attendance is required.';
        }

        dispatch({
            type: 'update',
            newState,
        });
    };

    const handleAttendanceDateBlur = (val) => {
        let isMoment = moment.isMoment(val);

        let newState = {
            showDateError: true,
        };

        if(isMoment) {
            newState.dateError = false;
            newState.dateErrorMessage = null;
        }

        dispatch({
            type: 'update',
            newState
        });
    };

    const handleAttendanceDateError = (err, val) => {
        let _err, errMsg;
        if(val === null || val?.toISOString?.() === null) {
            _err = true;
            errMsg = 'First Day of Attendance is Required.';
        } else if (err === 'minDate') {
            _err = true;
            errMsg = 'First Day of Attendance must be more recent.';
        } else if (err === 'maxDate') {
            _err = true;
            errMsg = 'First Day of Attendance must be in the past.';
        } else if (err === null) {
            _err = false;
            errMsg = null; 
        } else return;

        dispatch({
            type: 'update',
            newState: {
                dateError: _err,
                dateErrorMessage: errMsg,
            }
        });
    };

    let displayDateError = () => dispatch({
        type: 'update',
        newState: {
            showDateError: true,
        }
    });

    let displayAllErrors = () => dispatch({
        type: 'update',
        newState: {
            showDateError: true,
            showDiscountError: true,
            fees: state?.fees?.map(fee => {
                return {
                    ...fee,
                    showDescriptionError: true,
                    showAmountError: true,
                }
            })
        }
    });

    return {
        ...state,
        ...styles,
        initialize,
        addFee,
        handleFeeDescription,
        handleDescriptionBlur,
        handleFeeAmount,
        handleAmountBlur,
        handleDiscountChange,
        handleDiscountBlur,
        handleAttendanceDateChange,
        handleAttendanceDateBlur,
        handleAttendanceDateError,
        saveRow,
        autopopFees,
        deleteFee,
        displayDateError,
        displayAllErrors,
    };
};

export default useFees;
